// -----------------------------------------------------------------------------
// General aggregate slider used for example in Segments.tsx
// -----------------------------------------------------------------------------

import React, { useMemo, useRef, useLayoutEffect, useState, useEffect } from 'react';
import { Box, Slider, SxProps, Theme } from '@mui/material';

// --- Props -------------------------------------------------------------------

export type AggregateRangeSliderProps = {
    value: [number, number]; // inclusive [min,max]
    lastIndex: number; // highest index
    onChange: (next: [number, number]) => void;
    onChangeCommitted?: (next: [number, number]) => void;
    size?: 'small' | 'medium';
    step?: number;
    disabled?: boolean;
    sx?: SxProps<Theme>;
    wrapSelection?: boolean;

    // Visuals
    baseTrackThickness?: number;
    activeTrackThickness?: number;

    autoDetectPadding?: boolean; // align band to rail
    sidePaddingPx?: number; // fallback padding default 13

    sliderProps?: Omit<React.ComponentProps<typeof Slider>, 'value' | 'min' | 'max' | 'step' | 'onChange'> & {
        'data-testid'?: string;
    };

    showValues?: boolean;
    valueLabelFormatter?: (value: number) => string | number;
};

// --- Utils -------------------------------------------------------------------
function clamp(n: number, min: number, max: number) {
    return Math.min(Math.max(n, min), max);
}

// Get number of decimal places in step for proper rounding, up to 8 digits
function getStepPrecision(step: number) {
    if (!Number.isFinite(step)) return 0;
    const stepText = step.toString();
    const dotIndex = stepText.indexOf('.');
    if (dotIndex === -1) return 0;
    return Math.min(8, Math.max(0, stepText.length - dotIndex - 1));
}

// --- Component ---------------------------------------------------------------

const RangeSlider: React.FC<AggregateRangeSliderProps> = ({
    value,
    lastIndex,
    onChange,
    onChangeCommitted,
    size = 'small',
    step = 1,
    disabled,
    sx,
    wrapSelection = false,
    baseTrackThickness = 2,
    activeTrackThickness = 3,
    autoDetectPadding = true,
    sidePaddingPx = 13,
    sliderProps,
    showValues = false,
    valueLabelFormatter,
}) => {
    const sliderRootRef = useRef<HTMLSpanElement | null>(null);
    const bandContainerRef = useRef<HTMLDivElement | null>(null);

    // Optimistic local value during drag to keep UI in lockstep
    const [dragValue, setDragValue] = useState<[number, number] | null>(null);
    const displayValue = dragValue ?? value;

    const isDraggingBandRef = useRef(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isHover, setIsHover] = useState(false);

    const minValue = 0;
    const maxValue = Math.max(0, lastIndex);
    const stepSize = Number.isFinite(step) && step > 0 ? step : 1;
    const stepPrecision = useMemo(() => getStepPrecision(stepSize), [stepSize]);
    const roundToStep = (value: number) => {
        if (stepPrecision <= 0) return value;
        return Number(value.toFixed(stepPrecision));
    };
    const snapToStep = (value: number) => {
        const steps = Math.round((value - minValue) / stepSize);
        return roundToStep(minValue + steps * stepSize);
    };

    // Coalesce parent updates to once per frame
    const rafIdRef = useRef<number | null>(null);
    const pendingNextRef = useRef<[number, number] | null>(null);
    const scheduleParentChange = (next: [number, number]) => {
        pendingNextRef.current = next;
        if (rafIdRef.current == null) {
            rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;
                const v = pendingNextRef.current;
                pendingNextRef.current = null;
                if (v) onChange(v);
            });
        }
    };
    useEffect(
        () => () => {
            if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
        },
        []
    );

    const lastClientX = useRef(0);
    const accDeltaRef = useRef(0); // fractional steps accumulator

    // Measure rail padding so the band aligns to the real rail (no hover offset)
    const [measuredPad, setMeasuredPad] = useState<number | null>(null);
    const [railMetrics, setRailMetrics] = useState<{ top: number; height: number } | null>(null);
    useLayoutEffect(() => {
        if (!autoDetectPadding || !sliderRootRef.current) return;
        const root = sliderRootRef.current;
        const rail = root.querySelector<HTMLElement>('.MuiSlider-rail');
        if (!rail) return;
        const rootRect = root.getBoundingClientRect();
        const railRect = rail.getBoundingClientRect();
        const pad = Math.max(0, Math.round(railRect.left - rootRect.left));
        if (pad >= 0 && pad < 100) setMeasuredPad(pad);
        const top = Math.max(0, Math.round(railRect.top - rootRect.top));
        const height = Math.max(1, Math.round(railRect.height));
        setRailMetrics({ top, height });
    }, [autoDetectPadding, size, lastIndex]);
    const leftRightPad = measuredPad ?? sidePaddingPx;

    //band drag is now based on step counts
    const rangeSpan = Math.max(0, maxValue - minValue);
    const totalSteps = Math.max(1, Math.round(rangeSpan / stepSize));
    const rangeWidth = useMemo(() => Math.max(0, displayValue[1] - displayValue[0]), [displayValue]);
    const rangeWidthSteps = useMemo(() => Math.max(0, Math.round(rangeWidth / stepSize)), [rangeWidth, stepSize]);
    const alignedRangeWidth = rangeWidthSteps * stepSize;

    // Slightly thicker track while hover/drag
    const trackThickness = disabled ? baseTrackThickness : isHover || isDragging ? activeTrackThickness : baseTrackThickness;

    // --- Band drag handlers -----------------------------------------------------

    const onBandPointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
        if (disabled) return;
        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
        isDraggingBandRef.current = true;
        setIsDragging(true);
        setDragValue(value); // start from current external value
        lastClientX.current = e.clientX;
        accDeltaRef.current = 0;
    };

    const onBandPointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
        if (disabled || !isDraggingBandRef.current) return;

        const rect = bandContainerRef.current?.getBoundingClientRect();
        if (!rect || rect.width <= 0) return;

        const dx = e.clientX - lastClientX.current;
        lastClientX.current = e.clientX;

        const deltaFloat = (dx / rect.width) * totalSteps;
        accDeltaRef.current += deltaFloat;

        const stepInt = accDeltaRef.current > 0 ? Math.floor(accDeltaRef.current) : Math.ceil(accDeltaRef.current);
        if (stepInt === 0) return;
        accDeltaRef.current -= stepInt;

        const deltaValue = stepInt * stepSize;
        const nextMin = clamp(displayValue[0] + deltaValue, minValue, Math.max(minValue, maxValue - alignedRangeWidth));
        const snappedMin = snapToStep(nextMin);
        const nextMax = roundToStep(snappedMin + alignedRangeWidth);
        if (snappedMin === displayValue[0]) return;

        const next: [number, number] = [snappedMin, nextMax];

        // Immediate local update
        setDragValue(next);

        // Parent update once per animation frame
        scheduleParentChange(next);
    };

    const onBandPointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
        if (!isDraggingBandRef.current) return;
        (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
        isDraggingBandRef.current = false;
        setIsDragging(false);

        const finalVal = dragValue ?? value;
        setDragValue(null); // give control back to parent
        onChangeCommitted?.(finalVal);
    };

    // --- Layout in percentages --------------------------------------------------

    //Calculate percentages using minValue and rangeSpan, support non zero minimum (e.g. free view)
    const pctSpan = rangeSpan === 0 ? 1 : rangeSpan;
    const bandWidthPct = (Math.max(0, displayValue[1] - displayValue[0]) / pctSpan) * 100;
    const bandLeftPct = ((displayValue[0] - minValue) / pctSpan) * 100;
    const bandRightPct = Math.min(100, bandLeftPct + bandWidthPct);
    const showWrap = wrapSelection && displayValue[0] !== displayValue[1] && maxValue > 0;

    // --- Render ----------------------------------------------------------------

    const formatLabel = useMemo(() => valueLabelFormatter ?? ((v: number) => v), [valueLabelFormatter]);

    return (
        <Box sx={{ position: 'relative', ...sx }}>
            <Slider
                className="panel-slider"
                ref={sliderRootRef}
                valueLabelDisplay={showValues ? 'on' : 'off'}
                valueLabelFormat={(v) => formatLabel(v as number)}
                size={size}
                value={displayValue}
                min={minValue}
                max={maxValue}
                step={step}
                disabled={disabled}
                marks={false} // remove marks by default
                onChange={(_, v) => {
                    const next = v as [number, number];
                    setDragValue(next); // keep UI snappy even if parent is heavy
                    scheduleParentChange(next);
                }}
                onChangeCommitted={(_, v) => {
                    const next = v as [number, number];
                    setDragValue(null);
                    onChangeCommitted?.(next);
                }}
                disableSwap
                sx={{
                    width: '100%',
                    position: 'relative',

                    // Keep thumbs above the band so hover/focus work
                    '& .MuiSlider-thumb': {
                        zIndex: 2,
                        // Critical: disable position transitions while band-dragging to avoid lag
                        transition: isDragging ? 'none' : undefined,
                        willChange: isDragging ? 'left' : undefined,
                    },

                    // Grow track/rail height slightly on hover/drag and kill transitions while dragging
                    '& .MuiSlider-track, & .MuiSlider-rail': {
                        height: `${trackThickness}px`,
                        borderRadius: `${trackThickness}px`,
                        transition: isDragging ? 'none' : 'height 120ms ease',
                        willChange: isDragging ? 'left, width' : undefined,
                    },
                    '& .MuiSlider-track': {
                        opacity: showWrap ? 0 : 1,
                    },
                    '& .MuiSlider-markLabel': { display: 'none' },

                    cursor: disabled ? 'not-allowed' : isDragging ? 'grabbing' : 'default',
                    ...(sliderProps?.sx as object),
                }}
                {...sliderProps}
            />

            {/* Rail-aligned container */}
            <Box
                ref={bandContainerRef}
                sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${leftRightPad}px`,
                    right: `${leftRightPad}px`,
                    zIndex: 1, // below thumbs
                    pointerEvents: 'none',
                }}
            >
                {showWrap && (
                    <>
                        <Box
                            sx={(theme) => ({
                                position: 'absolute',
                                top: railMetrics ? `${railMetrics.top}px` : '50%',
                                transform: railMetrics ? 'none' : 'translateY(-50%)',
                                height: `${railMetrics?.height ?? trackThickness}px`,
                                borderRadius: `${trackThickness}px`,
                                left: 0,
                                width: `${Math.max(0, bandLeftPct)}%`,
                                backgroundColor: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
                                opacity: disabled ? 0.5 : 1,
                                pointerEvents: 'none',
                            })}
                        />
                        <Box
                            sx={(theme) => ({
                                position: 'absolute',
                                top: railMetrics ? `${railMetrics.top}px` : '50%',
                                transform: railMetrics ? 'none' : 'translateY(-50%)',
                                height: `${railMetrics?.height ?? trackThickness}px`,
                                borderRadius: `${trackThickness}px`,
                                left: `${bandRightPct}%`,
                                width: `${Math.max(0, 100 - bandRightPct)}%`,
                                backgroundColor: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
                                opacity: disabled ? 0.5 : 1,
                                pointerEvents: 'none',
                            })}
                        />
                    </>
                )}
                {/* Draggable band  */}
                <Box
                    onPointerDown={onBandPointerDown}
                    onPointerMove={onBandPointerMove}
                    onPointerUp={onBandPointerUp}
                    onPointerEnter={() => setIsHover(true)}
                    onPointerLeave={() => setIsHover(false)}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        height: `${trackThickness}px`,
                        borderRadius: `${trackThickness}px`,
                        left: `${bandLeftPct}%`,
                        width: `${bandWidthPct}%`,
                        pointerEvents: disabled ? 'none' : 'auto',
                        cursor: disabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
                    }}
                    aria-label="aggregate band"
                    role="slider"
                    aria-valuemin={displayValue[0]}
                    aria-valuemax={displayValue[1]}
                    aria-disabled={disabled ? 'true' : 'false'}
                />
            </Box>
        </Box>
    );
};

export default RangeSlider;
